/**
 * All application route paths defined as constants.
 * Import from here — never hard-code paths in components.
 *
 * Enterprise route hierarchy — three top-level sections, no overlap:
 *   /auth/*    → authentication (public-only)
 *   /admin/*   → admin console   (role === 'admin')
 *   /portal/*  → user portal      (role === 'user' OR 'admin')
 *
 * Legacy flat routes (/dashboard, /events, /users, /settings/*) were removed in
 * the route-separation refactor. AppRouter keeps thin <Navigate> redirects from
 * the old paths to their new homes for bookmark safety.
 */

export const ROUTES = {
  // ── Auth (public) ─────────────────────────────────────
  AUTH: {
    ROOT:     '/auth',
    LOGIN:    '/auth/login',
    REGISTER: '/auth/register',
    FORGOT:   '/auth/forgot-password',
    RESET:    '/auth/reset-password',
  },

  // ── Admin console (role === 'admin') ──────────────────
  ADMIN: {
    ROOT:      '/admin',
    DASHBOARD: '/admin/dashboard',

    EVENTS: {
      ROOT:   '/admin/events',
      LIST:   '/admin/events',
      CREATE: '/admin/events/create',
      DETAIL: (id = ':id') => `/admin/events/${id}`,
      EDIT:   (id = ':id') => `/admin/events/${id}/edit`,
    },

    USERS: {
      ROOT:   '/admin/users',
      LIST:   '/admin/users',
      CREATE: '/admin/users/create',
      DETAIL: (id = ':id') => `/admin/users/${id}`,
      EDIT:   (id = ':id') => `/admin/users/${id}/edit`,
    },

    // ── New canonical key (use this everywhere) ───────────
    ASSIGNMENTS: {
      ROOT:   '/admin/assignments',
      LIST:   '/admin/assignments',
      DETAIL: (id = ':id') => `/admin/assignments/${id}`,
    },

    // ── Legacy — kept only for AppRouter redirect safety ──
    REGISTRATIONS: {
      ROOT:   '/admin/registrations',
      LIST:   '/admin/registrations',
      DETAIL: (id = ':id') => `/admin/registrations/${id}`,
    },

    REPORTS: {
      ROOT:        '/admin/reports',
      OVERVIEW:    '/admin/reports/overview',
      EVENTS:      '/admin/reports/events',
      USERS:       '/admin/reports/users',
      ASSIGNMENTS: '/admin/reports/assignments',
      // Legacy alias kept for any existing bookmarks:
      REGISTRATIONS: '/admin/reports/assignments',
    },

    SETTINGS: {
      ROOT:          '/admin/settings',
      PROFILE:       '/admin/settings/profile',
      ACCOUNT:       '/admin/settings/account',
      ORGANIZATION:  '/admin/settings/organization',
      BRANDING:      '/admin/settings/branding',
      REGIONAL:      '/admin/settings/regional',
      NOTIFICATIONS: '/admin/settings/notifications',
      SECURITY:      '/admin/settings/security',
    },

    NOTIFICATIONS: {
      ROOT:             '/admin/notifications',
      LIST:             '/admin/notifications',
      TEMPLATES:        '/admin/notifications/templates',
      TEMPLATE_DETAIL:  (id = ':id') => `/admin/notifications/templates/${id}`,
      HISTORY:          '/admin/notifications/history',
    },

    // ── Communication (Email Templates live here now) ─────────────
    COMMUNICATION: {
      ROOT:            '/admin/communication',
      TEMPLATES:       '/admin/communication/templates',
      TEMPLATE_DETAIL: (id = ':id') => `/admin/communication/templates/${id}`,
    },

    CONNECTIONS: {
      ROOT:      '/admin/connections',
      LIST:      '/admin/connections',
      DETAIL:    (id = ':id') => `/admin/connections/${id}`,
      ANALYTICS: '/admin/connections/analytics',
    },

    // ── Outreach / Email Outreach ─────────────────────────
    EMAILS: {
      ROOT: '/admin/emails',
      SENT: '/admin/emails/sent',
    },

    COMPANIES: {
      ROOT:   '/admin/companies',
      LIST:   '/admin/companies',
      CREATE: '/admin/companies/create',
      DETAIL: (id = ':id') => `/admin/companies/${id}`,
      EDIT:   (id = ':id') => `/admin/companies/${id}/edit`,
    },

  },

  // ── User portal (role === 'user' OR 'admin') ──────────
  PORTAL: {
    ROOT:      '/portal',
    DASHBOARD: '/portal/dashboard',

    // Event detail kept for deep-links from My Events cards
    EVENT_DETAIL: (id = ':id') => `/portal/events/${id}`,

    MY_EVENTS:     '/portal/my-events',
    PROFILE:       '/portal/profile',
    SETTINGS:      '/portal/settings',
    NOTIFICATIONS: '/portal/notifications',

    SETTINGS_PAGES: {
      NOTIFICATIONS: '/portal/settings/notifications',
    },

    CONNECTIONS: {
      ROOT:   '/portal/connections',
      LIST:   '/portal/connections',
      CREATE: '/portal/connections/create',
      DETAIL: (id = ':id') => `/portal/connections/${id}`,
      EDIT:   (id = ':id') => `/portal/connections/${id}/edit`,
    },
  },

  // ── Admin Roles & Permissions ─────────────────────────
  ROLES: {
    ROOT:   '/admin/roles',
    LIST:   '/admin/roles',
    CREATE: '/admin/roles/create',
    DETAIL: (id = ':id') => `/admin/roles/${id}`,
    EDIT:   (id = ':id') => `/admin/roles/${id}/edit`,
  },

  // ── Misc ──────────────────────────────────────────────
  NOT_FOUND: '*',
  FORBIDDEN: '/403',
}

/**
 * Role-aware home path. Used by login, post-register, and root redirects so
 * each role lands on its own dashboard.
 */
export function homePathForRole(role) {
  const adminRoles = ['super_admin', 'admin']
  return adminRoles.includes(role) ? ROUTES.ADMIN.DASHBOARD : ROUTES.PORTAL.DASHBOARD
}
